import React from "react";
import { useState } from "react";

import Dialog from "@mui/material/Dialog";

import axios from "axios";
import { KeyboardEvent } from "react";
import { useEffect } from "react";

import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";

import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";

import Button from "@mui/material/Button";
import LoadingButton from "@mui/lab/LoadingButton";

import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import SyncLockOutlinedIcon from "@mui/icons-material/SyncLockOutlined";

import GetCSRFToken from "./GetCSRFToken";

type ChangePasswordDialogProps = {
  open: boolean;
  close: () => void;
};

function ChangePasswordDialog(props: ChangePasswordDialogProps) {
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword1, setNewPassword1] = useState("");
  const [newPassword2, setNewPassword2] = useState("");

  const [wrongPasswordCount, setWrongPasswordCount] = useState(0); // TODO: after 2 or 3 suggest a reset?
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showOldError, setShowOldError] = useState<string | undefined>(undefined);
  const [showNewError, setShowNewError] = useState<string | undefined>(undefined);
  const [showConfirmError, setShowConfirmError] = useState<string | undefined>(undefined);

  function clearFields() {
    setOldPassword("");
    setNewPassword1("");
    setNewPassword2("");
  }

  function clearErrors() {
    setShowOldError(undefined);
    setShowNewError(undefined);
    setShowConfirmError(undefined);
  }

  function handleCancel() {
    if (!loading) {
      clearFields();
      clearErrors();
      props.close();
    }
  }

  function handleSubmit() {
    setLoading(true);
    clearErrors();
    axios
      .post(
        "/api/user/change-password",
        {
          old_password: oldPassword,
          new_password1: newPassword1,
          new_password2: newPassword2,
        },
        {
          headers: { "X-CSRFToken": GetCSRFToken() },
        }
      )
      .then((res) => {
        setSuccess(true);
        axios
          .post(
            "/api/user/login",
            {
              username: res.data["username"],
              password: newPassword1,
            },
            {
              headers: { "X-CSRFToken": GetCSRFToken() },
            }
          )
          .then((res) => {
            setTimeout(() => setSuccess(false), 1000); // clear the successful signout state 1 second after change password
            // TODO: this is a bit flaky on logging back in...should we just log people out? can leave the app in a bit odd of a state where they are logged in but they aren't?
            // TODO: maybe hit the login backend endpoint here again? and say password changed...logging back in now?
            setLoading(false);
            handleCancel();
            // TODO: something with this response, a change password successful?
            // TODO: flash a success snackbar? log them out?});
            // TODO: something with this response, a log in successful?
            // TODO: propogate upward to force a reload?
            // TODO: flash a success message?
          })
          .catch((err) => {
            if (process.env.NODE_ENV === "development") {
              console.log(err);
            } // TODO: log user out and propagate a logout
          });
      })
      .catch((err) => {
        clearFields();
        [
          {
            key: "old",
            fn: (newError: string | undefined) => {
              if (newError !== undefined) {
                setWrongPasswordCount(wrongPasswordCount + 1);
              }
              setShowOldError(newError);
            },
          },
          {
            key: "new",
            fn: setShowNewError,
          },
          {
            key: "confirm",
            fn: setShowConfirmError,
          },
        ].map((error_details_handlers) => error_details_handlers.fn(err.response.data[error_details_handlers.key]));
        setLoading(false);
      });
  }

  function catchEnterKeyDownToSubmit(e: KeyboardEvent) {
    if (e.key === "Enter") {
      handleSubmit();
    }
  }

  return (
    <Dialog open={props.open} onClose={handleCancel} disableRestoreFocus fullWidth>
      <DialogTitle> Change Password </DialogTitle>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "row",
        }}
      >
        <FormControl
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            flex: 1,
            paddingTop: 1,
          }}
        >
          <TextField
            variant="outlined"
            type="password"
            error={showOldError !== undefined}
            fullWidth
            value={oldPassword}
            onChange={(newValue) => {
              setShowOldError(undefined);
              setOldPassword(newValue.target.value);
            }}
            label="Old Password"
            autoComplete="current-password"
            autoFocus
            disabled={loading}
            onKeyDown={catchEnterKeyDownToSubmit}
            helperText={wrongPasswordCount < 3 ? showOldError : "Would you like to reset your password instead?"} // TODO: force logout here instead
          />
          <TextField
            variant="outlined"
            type="password"
            error={showNewError !== undefined}
            autoComplete="new-password"
            fullWidth
            value={newPassword1}
            onChange={(newValue) => {
              setShowNewError(undefined);
              setNewPassword1(newValue.target.value);
            }}
            disabled={loading}
            onKeyDown={catchEnterKeyDownToSubmit}
            label="New Password"
            helperText={showNewError}
          />
          <TextField
            variant="outlined"
            type="password"
            error={showConfirmError !== undefined}
            autoComplete="new-password"
            fullWidth
            value={newPassword2}
            onChange={(newValue) => {
              setShowConfirmError(undefined);
              setNewPassword2(newValue.target.value);
            }}
            disabled={loading}
            onKeyDown={catchEnterKeyDownToSubmit}
            label="Confirm New Password"
            helperText={showConfirmError}
          />
        </FormControl>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between" }}>
        <Button color="error" startIcon={<CloseIcon />} onClick={handleCancel} disabled={loading || success}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          color={success ? "success" : undefined}
          startIcon={success ? <CheckIcon /> : <SyncLockOutlinedIcon />}
          loading={loading && !success}
          onClick={() => {
            if (!success) {
              handleSubmit();
            }
          }}
        >
          {success ? "Password Changed!" : "Change Password"}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

export default ChangePasswordDialog;
